import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';
import { keepsCrossSiteCookies } from './crossSiteCookieProbe';

const COGNITO_COOKIE_PREFIX = 'CognitoIdentityServiceProvider.';
const REWRITE_MARKER_KEY = 'popp-cognito-cookies-same-site';
const REWRITE_MARKER = 'none';
const BULLHORN_ORIGIN = /^https:\/\/([a-z0-9-]+\.)*bullhornstaffing\.com$/;

interface TokenStorageOptions {
  hostname: string;
  cookieDomain?: string;
  isFramed: boolean;
  ancestorOrigins: readonly string[];
}

const isFramedOnlyByBullhorn = (ancestorOrigins: readonly string[]): boolean =>
  ancestorOrigins.length > 0 &&
  ancestorOrigins.every((origin) => BULLHORN_ORIGIN.test(origin));

// Use CookieStorage for auth tokens so they're shared across subdomains
// (e.g. app.popp.com and admin.popp.com) via a common COOKIE_DOMAIN.
// In a frame, use the cookies only inside Bullhorn, where popp-web-v2 needs them,
// and only when the browser keeps third-party cookies. Other hosts, such as Tux,
// keep localStorage: Safari and Firefox block the cookies there. Firefox has no
// ancestorOrigins, so it also keeps localStorage. See ADR embedded-auth-cookies in
// popp-web-v2.
export const configureTokenStorage = ({
  hostname,
  cookieDomain,
  isFramed,
  ancestorOrigins
}: TokenStorageOptions): Promise<void> => {
  const hostnameMatchesCookieDomain =
    cookieDomain &&
    hostname !== 'localhost' &&
    (hostname === cookieDomain || hostname.endsWith(`.${cookieDomain}`));

  if (!hostnameMatchesCookieDomain) return Promise.resolve();
  if (
    isFramed &&
    (!isFramedOnlyByBullhorn(ancestorOrigins) ||
      !keepsCrossSiteCookies(cookieDomain))
  ) {
    return Promise.resolve();
  }

  const cookieStorage = new CookieStorage({
    domain: cookieDomain,
    path: '/',
    sameSite: 'none',
    secure: true
  });
  cognitoUserPoolsTokenProvider.setKeyValueStorage(cookieStorage);
  return rewriteCognitoCookies(cookieStorage);
};

const readCognitoCookies = (): Map<string, string> =>
  new Map(
    document.cookie
      .split('; ')
      .map((cookie): [string, string] => {
        const separator = cookie.indexOf('=');
        return [cookie.slice(0, separator), cookie.slice(separator + 1)];
      })
      .filter(([name]) => name.startsWith(COGNITO_COOKIE_PREFIX))
  );

type CookieStoreLike = {
  getAll: () => Promise<{ name: string; sameSite?: string }[]>;
};

// Only the Cookie Store API reports SameSite; document.cookie does not.
// null means the browser cannot tell, for example Firefox.
const readLaxCookieNames = async (): Promise<Set<string> | null> => {
  const { cookieStore } = window as unknown as {
    cookieStore?: CookieStoreLike;
  };
  if (!cookieStore) return null;
  try {
    const cookies = await cookieStore.getAll();
    return new Set(
      cookies
        .filter(
          ({ name, sameSite }) =>
            name.startsWith(COGNITO_COOKIE_PREFIX) && sameSite !== 'none'
        )
        .map(({ name }) => name)
    );
  } catch {
    return null;
  }
};

const readRewriteMarker = (): string | null => {
  try {
    return localStorage.getItem(REWRITE_MARKER_KEY);
  } catch {
    return null;
  }
};

const writeRewriteMarker = (): void => {
  try {
    localStorage.setItem(REWRITE_MARKER_KEY, REWRITE_MARKER);
  } catch {
    return;
  }
};

// A session from before sameSite 'none' keeps its Lax cookies until Amplify writes
// them again, so write them again now: only the Lax ones where the browser reports
// SameSite, else one time per browser, recorded in localStorage.
// document.cookie gives encoded names, and CookieStorage encodes the name again, so
// decode the name first. An earlier release did not, and each load added a copy of
// each token under a name with %25 (for example %2540 for @). Remove those copies
// on every load.
// Another tab can sign out or refresh a token during this loop. It writes only a
// cookie that still has the value it read.
// A cookie with a malformed percent escape stays as it is, so it cannot stop startup.
// TODO: remove when every Lax Cognito cookie has expired (365 days after release).
const rewriteCognitoCookies = async (
  cookieStorage: CookieStorage
): Promise<void> => {
  const laxCookieNames = await readLaxCookieNames();
  const isLax = (rawName: string): boolean =>
    laxCookieNames
      ? laxCookieNames.has(rawName)
      : readRewriteMarker() !== REWRITE_MARKER;
  const cookies = readCognitoCookies();
  for (const [rawName, value] of cookies) {
    if (readCognitoCookies().get(rawName) !== value) continue;
    try {
      const name = decodeURIComponent(rawName);
      if (isDoubleEncodedCopy(rawName, name, cookies)) {
        void cookieStorage.removeItem(name);
        continue;
      }
      if (!isLax(rawName)) continue;
      void cookieStorage.setItem(name, decodeURIComponent(value));
    } catch {
      continue;
    }
  }
  if (!laxCookieNames) writeRewriteMarker();
};

// A username with a literal % also encodes to %25, so %25 alone does not mark a
// copy. Amplify reads only the tokens of the LastAuthUser username.
// Names are CognitoIdentityServiceProvider.<clientId>.<username>.<key>.
const isDoubleEncodedCopy = (
  rawName: string,
  name: string,
  cookies: Map<string, string>
): boolean => {
  if (!rawName.includes('%25')) return false;
  const rest = name.slice(COGNITO_COOKIE_PREFIX.length);
  const clientId = rest.slice(0, rest.indexOf('.'));
  const username = rest.slice(clientId.length + 1, rest.lastIndexOf('.'));
  const lastAuthUser = cookies.get(
    `${COGNITO_COOKIE_PREFIX}${clientId}.LastAuthUser`
  );
  return (
    lastAuthUser === undefined || decodeURIComponent(lastAuthUser) !== username
  );
};
